import clsx from 'clsx';
import { ChangeEvent, FC, useState } from 'react';
import { IInputComponent, ISelectOption, InputField as InputFieldType } from '../../../types';
import { CountryCode, countryCodes } from '../../../utils/phone';
import PasswordHints from '../../password-hints';
import Field from '../field';
import Select from '../select';
import styles from './styles.module.css';

interface IInputFieldPhone extends Omit<InputFieldType, 'value' | 'onChange'> {
  defaultPhone?: string;
  onChange: (countryCode: CountryCode, phoneNumber: string) => void;
}

const InputComponent: FC<IInputComponent> = props => {
  const {
    type = 'text',
    className = '',
    iconLeft,
    iconRight,
    controlProps = {},
    fullwidth = false,
    ...rest
  } = props;

  const iconsLeftClass = iconLeft ? 'has-icons-left' : '';
  const iconsRightClass = iconRight ? 'has-icons-right' : '';
  const fullwidthClass = fullwidth ? 'is-fullwidth' : '';

  return (
    <div
      className={clsx('control', fullwidthClass, iconsLeftClass, iconsRightClass)}
      {...controlProps}>
      <input type={type} className={clsx(styles.input, fullwidthClass, className)} {...rest} />

      {/* handle possible icons */}
      {Boolean(iconLeft) && <span className="icon is-left">{iconLeft}</span>}

      {Boolean(iconRight) && <span className="icon is-right">{iconRight}</span>}
    </div>
  );
};

const InputField: FC<InputFieldType> = props => {
  const {
    label,
    labelProps,
    fullwidth = false,
    required = false,
    errorMessage,
    ...inputProps
  } = props;

  return (
    <Field
      label={label}
      labelProps={labelProps}
      fullwidth={fullwidth}
      required={required}
      errorMessage={errorMessage}>
      <InputComponent fullwidth={fullwidth} required={required} {...inputProps} />
    </Field>
  );
};

const InputPassword: FC<InputFieldType> = props => {
  const {
    label,
    labelProps,
    fullwidth = false,
    value = '',
    required = false,
    errorMessage,
    ...inputProps
  } = props;

  return (
    <Field
      label={label}
      labelProps={labelProps}
      fullwidth={fullwidth}
      required={required}
      errorMessage={errorMessage}>
      <InputComponent
        fullwidth={fullwidth}
        type="password"
        value={value}
        maxLength={35}
        required={required}
        {...inputProps}
      />
      <PasswordHints pass={value as string} />
    </Field>
  );
};

const InputPhone: FC<IInputFieldPhone> = props => {
  const {
    label,
    labelProps,
    fullwidth = false,
    defaultPhone = '',
    disabled,
    required = false,
    errorMessage,
    onChange,
    ...inputProps
  } = props;
  // normalize incoming default if provided
  let defaultPhoneNumber = '';
  let defaultCountryCode: CountryCode = countryCodes[0].value;

  // NOTE: this is specific to our use-case and probably
  // would not handle all possible country codes correctly.
  if (/^\+/.test(defaultPhone)) {
    const found = countryCodes.find(c => c.regex.test(defaultPhone));
    if (found) {
      defaultCountryCode = found.value;
      defaultPhoneNumber = defaultPhone.replace(found.value, '');
    }
  } else {
    defaultPhoneNumber = defaultPhone;
  }

  const [phone, setPhone] = useState(defaultPhoneNumber);
  const [countryCode, setCountryCode] = useState(defaultCountryCode);

  const handleSetPhone = (e: ChangeEvent<HTMLInputElement>) => {
    const cleaned = (e.target.value || '').replace(/[^0-9]/g, '');
    setPhone(cleaned);
    onChange(countryCode, cleaned);
  };

  const handleSetCountryCode = (e: ChangeEvent<HTMLSelectElement>) => {
    const cc = e.target.value as CountryCode;
    setCountryCode(cc);
    onChange(cc, phone);
  };

  return (
    <Field
      label={label}
      labelProps={labelProps}
      fullwidth={fullwidth}
      required={required}
      errorMessage={errorMessage}>
      <div className={clsx(styles.inputPhoneWrapper)}>
        <Select.Component
          value={countryCode}
          options={countryCodes as unknown as ISelectOption[]}
          onChange={handleSetCountryCode}
          fullwidth={false}
          disabled={disabled}
          className={clsx(styles.selectCountryCode)}
        />
        <InputComponent
          type="input"
          className={clsx(styles.inputPhone, fullwidth ? '' : styles.inputPhoneWithMargin)}
          value={phone}
          maxLength={14}
          onChange={handleSetPhone}
          disabled={disabled}
          required={required}
          {...inputProps}
        />
      </div>
    </Field>
  );
};

export default {
  Component: InputComponent,
  Field: InputField,
  Password: InputPassword,
  Phone: InputPhone,
};
